import { bind } from '@react-rxjs/core'
import { createSignal } from '@react-rxjs/utils'
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  filter,
  map,
  merge,
  Observable,
  skipWhile,
  startWith,
  Subject,
  switchMap,
  take,
} from 'rxjs'
import { fromFetch } from 'rxjs/fetch'

import {
  heroTextOptionBySlugQuery,
  heroTextOptionsQuery,
} from '../../../groq/sanity-dispensaries/dispensary'
import { SanityHeroTextOptionDocument } from '../../../groq/sanity-dispensaries/types'
import { isNotNull } from '../../../utils/collectionTools'
import { coerceCaughtToLeft } from '../../../utils/rx/errors'
import { mapRight, switchMapRight, tapRight } from '../../../utils/rx/operators'
import { latestAccessToken$ } from '../../auth/state'
import { fetchResponseToJsonResult, FetchResult, headers } from '../../fetch'
import { PostHeroTextOptionInputData } from '../../sanity/types'
import { dispensariesClientLive, fetchQuery } from '../client'
import { latestDispensarySlug$ } from '../state'

const dispensaryHeroTextOptionTickCreate$ = new Subject<void>()
const dispensaryHeroTextOptionTickUpdate$ = new BehaviorSubject<void>(void 0)
const dispensaryHeroTextOptionTick$ = merge(
  dispensaryHeroTextOptionTickCreate$,
  dispensaryHeroTextOptionTickUpdate$,
)

const [upsertHeroTextOptionInput$, setUpsertHeroTextOptionInput] =
  createSignal<Omit<PostHeroTextOptionInputData, 'slug'>>()

export { setUpsertHeroTextOptionInput }

export const [useDispensaryHeroTextOption, dispensaryHeroTextOption$] = bind(
  latestDispensarySlug$.pipe(
    filter(isNotNull),
    switchMap((slug) =>
      dispensaryHeroTextOptionTick$.pipe(
        // refetch when `dispensaryHeroTextOptionTick$` changes
        switchMap(() =>
          fetchQuery(heroTextOptionBySlugQuery, { slug })(
            // skip CDN when fetching data for management section
            dispensariesClientLive,
          ),
        ),
      ),
    ),
  ),
)

const fromHeroTextOptionRequest = ({
  body,
  method,
}: {
  body: PostHeroTextOptionInputData
  method: 'POST' | 'PUT'
}) =>
  latestAccessToken$.pipe(
    filter(isNotNull),
    take(1),
    switchMap((accessToken) =>
      fromFetch('/api/sanity-dispensaries/hero-text-option', {
        body: JSON.stringify(body),
        method,
        headers: {
          ...headers.acceptJson,
          ...headers.authorization(accessToken),
          ...headers.contentTypeJson,
        },
      }),
    ),
    switchMap(fetchResponseToJsonResult),
    catchError(coerceCaughtToLeft),
  )

export const heroTextOptionUpsertFactory: (
  upsertInput: PostHeroTextOptionInputData,
) => Observable<FetchResult<Observable<FetchResult<SanityHeroTextOptionDocument | null>>>> = (
  upsertInput,
) =>
  // check for existing option to determine whether POST or PUT
  dispensaryHeroTextOption$.pipe(
    // only interested in the first value
    take(1),
    switchMapRight((existing) =>
      fromHeroTextOptionRequest({
        // update existing or create new if none found
        method: existing.data ? 'PUT' : 'POST',
        body: upsertInput,
      }).pipe(
        tapRight(() => {
          if (existing) {
            dispensaryHeroTextOptionTickUpdate$.next()
          } else {
            dispensaryHeroTextOptionTickCreate$.next()
          }
        }),
        mapRight(() => ({
          _tag: 'Right' as const,
          // return observable that starts with the first result not equal to existing result
          data: dispensaryHeroTextOption$.pipe(skipWhile((result) => result === existing)),
        })),
      ),
    ),
  )

export const [useSanityHeroTextOptionUpsert, sanityHeroTextOptionUpsert$] = bind(
  combineLatest([latestDispensarySlug$.pipe(filter(isNotNull)), upsertHeroTextOptionInput$]).pipe(
    switchMap(([slug, upsertInput]) =>
      heroTextOptionUpsertFactory({ ...upsertInput, slug }).pipe(
        map((result) => ({
          loading: false,
          result,
        })),
        startWith({
          loading: true,
          result: undefined,
        }),
      ),
    ),
  ),
  {
    loading: false,
    result: undefined,
  },
)

export const [useManagedHeroTextOptions] = bind(
  fetchQuery(
    heroTextOptionsQuery,
    undefined,
  )(
    // skip CDN when fetching data for management section
    dispensariesClientLive,
  ),
)
